import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoOperacao, IExtratoPdfConferencia } from '../../interfaces/IArquivoOperacao';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ExtratoBancarioService from '../../services/ExtratoBancarioService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { ISugestaoContaBancaria } from '../../interfaces/IModuloBancario';

const ACCEPTED_EXTENSIONS = '.ofx,.pdf';
const PER_PAGE = 10;
// DEVN3-686: enquanto um PDF está em análise de custo ou sendo lido pela IA, a lista se atualiza sozinha.
const INTERVALO_ATUALIZACAO_MS = 4000;

type Badge = { label: string; color: string; bg: string };

const badge = (label: string, color: string): Badge => ({ label, color, bg: `${color}22` });

function statusInfo(arquivo: IArquivoOperacao): Badge {
    if (arquivo.excluido)
        return badge(arquivo.pdf?.status === 'DESCARTADO' ? 'DESCARTADO' : 'EXCLUÍDO', '#6c757d');

    // DEVN3-767: extrato de conta bancária cadastrada no Módulo Bancário — continuidade de saldo.
    switch (arquivo.contaBancaria?.situacao) {
        case 'RETIDO': return badge('RETIDO', '#d9534f');
        case 'AGUARDANDO_CONTA': return badge('AGUARDANDO CONTA', '#fd7e14');
        case 'AGUARDANDO_SALDO': return badge('CONFIRMAR SALDO', '#fd7e14');
    }

    // Extrato em PDF: o status vem da leitura por IA, não das flags do arquivo.
    if (arquivo.pdf) {
        switch (arquivo.pdf.status) {
            case 'ANALISANDO': return badge('CALCULANDO CUSTO', '#f0ad4e');
            case 'AGUARDANDO_DECISAO': return badge('AGUARDANDO DECISÃO', '#009ef7');
            case 'APROVADO': return badge('PRONTO PARA PROCESSAR', '#7239ea');
            case 'EXTRAINDO':
                return badge(arquivo.pdf.totalLotes > 0
                    ? `LENDO ${Math.min(arquivo.pdf.lotesConcluidos + 1, arquivo.pdf.totalLotes)}/${arquivo.pdf.totalLotes}`
                    : 'LENDO', '#f0ad4e');
            case 'AGUARDANDO_CONFIRMACAO':
                return badge(arquivo.pdf.conferencia?.tipo === 'SEM_SALDO' ? 'SEM CONFERÊNCIA' : 'DIVERGÊNCIA', '#fd7e14');
            case 'IMPORTADO': return badge('CONCLUÍDO', '#5cb85c');
            case 'DESCARTADO': return badge('DESCARTADO', '#6c757d');
            case 'ERRO': return badge('ERRO', '#d9534f');
        }
    }

    if (arquivo.arquivoErro) return badge('ERRO', '#d9534f');
    if (arquivo.arquivoProcessando) return badge('PROCESSANDO', '#f0ad4e');
    if (arquivo.arquivoProcessou) return badge('CONCLUÍDO', '#5cb85c');
    if (arquivo.arquivoIniciouProcesso) return badge('AGUARDANDO', '#009ef7');
    return badge('PENDENTE', '#6c757d');
}

function usd(valor: number): string {
    if (valor > 0 && valor < 0.01) return '< US$ 0,01';
    return `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function brl(valor: number | null | undefined): string {
    if (valor === null || valor === undefined) return '—';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function custoInfo(arquivo: IArquivoOperacao): { texto: string; detalhe?: string } {
    const pdf = arquivo.pdf;
    if (!pdf) return { texto: '—' };
    if (pdf.status === 'ANALISANDO') return { texto: '…' };
    if (pdf.custoRealUsd > 0 && pdf.status !== 'EXTRAINDO') return { texto: usd(pdf.custoRealUsd), detalhe: 'custo real' };
    if (pdf.custoMaximoUsd > 0) return { texto: usd(pdf.custoProvavelUsd), detalhe: `previsto · até ${usd(pdf.custoMaximoUsd)}` };
    return { texto: '—' };
}

const CONFERENCIA_LABEL: Record<IExtratoPdfConferencia['tipo'], string> = {
    LINHA: 'saldo conferido linha a linha',
    DIA: 'saldo conferido por dia',
    TOTAL: 'conferido pelo saldo inicial e final',
    SEM_SALDO: 'o PDF não traz saldo para conferir',
};

// Arquivo que entra no próximo "Iniciar Processamento".
function prontoParaProcessar(a: IArquivoOperacao): boolean {
    if (a.excluido || a.arquivoProcessou || a.arquivoErro || a.arquivoProcessando) return false;
    return a.pdf ? a.pdf.status === 'APROVADO' : true;
}

const pdfSemDecisao = (a: IArquivoOperacao) =>
    !a.excluido && (a.pdf?.status === 'ANALISANDO' || a.pdf?.status === 'AGUARDANDO_DECISAO');

const pdfEmAndamento = (a: IArquivoOperacao) =>
    !a.excluido && (a.pdf?.status === 'ANALISANDO' || a.pdf?.status === 'EXTRAINDO');

interface Props {
    esteiraId: number;
    cartaoId: number;
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

function ModalExtratoBancario({ esteiraId, cartaoId, show, onHide, onSuccess }: Props) {
    const [arquivos, setArquivos] = useState<IArquivoOperacao[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [iniciando, setIniciando] = useState(false);
    const [excluindo, setExcluindo] = useState(false);
    const [decidindoId, setDecidindoId] = useState<number | null>(null);
    const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // silencioso = atualização automática: não mostra "Carregando" nem desmarca o que o usuário selecionou.
    const buscar = useCallback((silencioso: boolean = false) => {
        if (!esteiraId || !cartaoId) return;
        if (!silencioso) setLoading(true);
        ArquivoOperacaoService.listarPorCartao(esteiraId, cartaoId, page, PER_PAGE)
            .then(res => {
                const extratos = res.items.filter((a: IArquivoOperacao) => a.tipo === 'EXTRATO_BANCARIO');
                setArquivos(extratos);
                setTotal(extratos.length);
                setTotalPages(res.totalPages);
                if (!silencioso) setSelecionados(new Set());
            })
            .catch(err => console.error('Erro ao buscar extratos:', err))
            .finally(() => { if (!silencioso) setLoading(false); });
    }, [esteiraId, cartaoId, page]);

    useEffect(() => {
        if (show && esteiraId && cartaoId) {
            buscar();
        }
    }, [show, esteiraId, cartaoId, buscar]);

    const temPdfEmAndamento = arquivos.some(pdfEmAndamento);

    useEffect(() => {
        if (!show || !temPdfEmAndamento) return;
        const timer = setInterval(() => buscar(true), INTERVALO_ATUALIZACAO_MS);
        return () => clearInterval(timer);
    }, [show, temPdfEmAndamento, buscar]);

    const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        let erros = 0;
        let sugestao: { arquivoId: number; dados: ISugestaoContaBancaria } | null = null;

        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                const registrado = await ArquivoOperacaoService.registrar(esteiraId, 'EXTRATO_BANCARIO', generatedFileName, file.name, cartaoId);
                // DEVN3-767: OFX de conta ainda não cadastrada no Módulo Bancário — oferece o cadastro.
                if (!sugestao && file.name.toLowerCase().endsWith('.ofx')) {
                    const dados = await ModuloBancarioService.sugestao(registrado.id).catch(() => null);
                    if (dados) sugestao = { arquivoId: registrado.id, dados };
                }
            } catch {
                erros++;
            }
        }

        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (erros > 0) {
            Swal.fire({ text: `${erros} arquivo(s) falharam no envio.`, icon: 'warning', timer: 3000, showConfirmButton: false });
        } else {
            Swal.fire({ text: 'Arquivo(s) enviado(s) com sucesso!', icon: 'success', timer: 2000, showConfirmButton: false });
        }

        if (sugestao && erros === 0) {
            const { dados, arquivoId } = sugestao;
            const { isConfirmed } = await Swal.fire({
                title: 'Cadastrar a conta bancária?',
                html: `O extrato é da conta <b>Banco ${dados.banco}${dados.agencia ? ` · Agência ${dados.agencia}` : ''} · Conta ${dados.conta}</b>.<br/>
                    Cadastrada no Módulo Bancário, cada novo extrato desta conta passa a ter o saldo inicial conferido com o saldo final do anterior.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Cadastrar',
                cancelButtonText: 'Agora não',
            });
            if (isConfirmed) {
                try {
                    await ModuloBancarioService.aceitarSugestao(arquivoId, {
                        empresaId: dados.empresaId, esteiraId, banco: dados.banco, agencia: dados.agencia,
                        conta: dados.conta, contaContabil: dados.contaContabil ?? '', descricao: '', ativo: true,
                    });
                    Swal.fire({ text: 'Conta bancária cadastrada.', icon: 'success', timer: 2000, showConfirmButton: false });
                } catch (err: any) {
                    Swal.fire({ text: err?.response?.data?.message ?? 'Não foi possível cadastrar a conta.', icon: 'error' });
                }
            }
        }

        buscar();
    }, [esteiraId, cartaoId, buscar]);

    const handleIniciarProcessamento = useCallback(async () => {
        if (!arquivos.some(prontoParaProcessar)) {
            Swal.fire({ text: 'Nenhum arquivo pendente para processar.', icon: 'warning', timer: 3000, showConfirmButton: false });
            return;
        }

        setIniciando(true);
        try {
            await ExtratoBancarioService.iniciarProcessamento(esteiraId, cartaoId);
            Swal.fire({ text: 'Processamento iniciado! O cartão será movido para Conciliado Parcial ao concluir.', icon: 'success', timer: 3500, showConfirmButton: false });
            onHide();
            onSuccess();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao iniciar processamento.', icon: 'error', timer: 4000, showConfirmButton: false });
        } finally {
            setIniciando(false);
        }
    }, [esteiraId, cartaoId, arquivos, onHide, onSuccess]);

    const handleDecidirPdf = useCallback(async (arquivo: IArquivoOperacao, processar: boolean) => {
        if (!processar) {
            const confirm = await Swal.fire({
                title: 'Descartar este PDF?',
                text: 'O arquivo não será lido pela IA e nenhum custo será gerado.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Descartar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#d9534f',
            });
            if (!confirm.isConfirmed) return;
        }

        setDecidindoId(arquivo.id);
        try {
            await ExtratoBancarioService.decidirPdf(arquivo.id, processar);
            buscar(true);
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao registrar a decisão.', icon: 'error', timer: 4000, showConfirmButton: false });
        } finally {
            setDecidindoId(null);
        }
    }, [buscar]);

    const handleConfirmarPdf = useCallback(async (arquivo: IArquivoOperacao, acao: 'IMPORTAR' | 'RELER' | 'DESCARTAR') => {
        const conferencia = arquivo.pdf?.conferencia;
        const textos = {
            IMPORTAR: {
                title: 'Importar mesmo assim?',
                text: conferencia?.tipo === 'SEM_SALDO'
                    ? `Serão gravados ${conferencia.quantidade} lançamento(s) sem conferência de saldo. Confira a quantidade e os totais com o PDF antes de confirmar.`
                    : `Serão gravados ${conferencia?.quantidade ?? 0} lançamento(s) mesmo com a diferença de saldo apontada.`,
                confirmButtonText: 'Importar',
            },
            RELER: {
                title: 'Reler as páginas divergentes?',
                text: 'A IA lê de novo apenas as páginas em que o saldo não fechou. Essa releitura gera um novo custo, proporcional a essas páginas.',
                confirmButtonText: 'Reler',
            },
            DESCARTAR: {
                title: 'Descartar este PDF?',
                text: 'A leitura feita será desconsiderada e nenhum lançamento deste arquivo será gravado.',
                confirmButtonText: 'Descartar',
            },
        }[acao];

        const confirm = await Swal.fire({
            ...textos,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonColor: acao === 'DESCARTAR' ? '#d9534f' : undefined,
        });
        if (!confirm.isConfirmed) return;

        setDecidindoId(arquivo.id);
        try {
            await ExtratoBancarioService.confirmarPdf(arquivo.id, acao);
            if (acao === 'DESCARTAR') {
                buscar(true);
            } else {
                Swal.fire({ text: 'Processamento iniciado! O cartão será movido para Conciliado Parcial ao concluir.', icon: 'success', timer: 3500, showConfirmButton: false });
                onHide();
                onSuccess();
            }
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao confirmar o extrato.', icon: 'error', timer: 4000, showConfirmButton: false });
        } finally {
            setDecidindoId(null);
        }
    }, [buscar, onHide, onSuccess]);

    const toggleSelecionado = (id: number) => {
        setSelecionados(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleExcluir = useCallback(async () => {
        const ids = Array.from(selecionados);
        if (ids.length === 0) return;

        const plural = ids.length > 1;
        const confirm = await Swal.fire({
            title: plural ? 'Excluir extratos importados?' : 'Excluir extrato importado?',
            text: plural
                ? 'Deseja realmente excluir estes extratos importados? Esta ação removerá os lançamentos bancários associados a estas importações.'
                : 'Deseja realmente excluir este extrato importado? Esta ação removerá os lançamentos bancários associados a esta importação.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Excluir',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d9534f',
        });
        if (!confirm.isConfirmed) return;

        setExcluindo(true);
        try {
            await ExtratoBancarioService.excluirExtratos(esteiraId, cartaoId, ids);
            Swal.fire({ text: 'Exclusão iniciada. O cartão volta para Em Conferência ao concluir.', icon: 'success', timer: 3500, showConfirmButton: false });
            onHide();
            onSuccess();
        } catch (err: any) {
            Swal.fire({ text: err?.response?.data?.message || 'Erro ao excluir extrato(s).', icon: 'error', timer: 4000, showConfirmButton: false });
        } finally {
            setExcluindo(false);
        }
    }, [esteiraId, cartaoId, selecionados, onHide, onSuccess]);

    const handlePage = (p: number) => {
        if (p >= 1 && p <= totalPages) setPage(p);
    };

    const ocupado = iniciando || uploading || excluindo || decidindoId !== null;
    const temPendentes = arquivos.some(prontoParaProcessar);
    const temPdfSemDecisao = arquivos.some(pdfSemDecisao);

    const renderConferencia = (arquivo: IArquivoOperacao, conferencia: IExtratoPdfConferencia) => {
        const diferenca = conferencia.saldoFinalInformado !== null && conferencia.saldoFinalCalculado !== null
            ? conferencia.saldoFinalInformado - conferencia.saldoFinalCalculado
            : null;

        return (
            <tr>
                <td></td>
                <td colSpan={5} style={{ paddingTop: 0 }}>
                    <div style={{ backgroundColor: '#fff8f0', border: '1px solid #fd7e1455', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
                        <div className='fw-bold mb-1'>
                            {conferencia.tipo === 'SEM_SALDO'
                                ? 'Leitura sem conferência: o PDF não traz saldo para comparar.'
                                : `A soma dos lançamentos lidos não fechou com o saldo do PDF em ${conferencia.totalDivergencias} ponto(s).`}
                        </div>
                        <div className='d-flex flex-wrap gap-4 mb-1'>
                            <span>Lançamentos: <b>{conferencia.quantidade}</b></span>
                            <span>Entradas: <b>{brl(conferencia.totalEntradas)}</b></span>
                            <span>Saídas: <b>{brl(conferencia.totalSaidas)}</b></span>
                            {conferencia.tipo !== 'SEM_SALDO' && (
                                <>
                                    <span>Saldo final no PDF: <b>{brl(conferencia.saldoFinalInformado)}</b></span>
                                    <span>Calculado: <b>{brl(conferencia.saldoFinalCalculado)}</b></span>
                                    {diferenca !== null && <span>Diferença: <b style={{ color: '#d9534f' }}>{brl(diferenca)}</b></span>}
                                </>
                            )}
                        </div>
                        {conferencia.divergencias.slice(0, 3).map((d, i) => (
                            <div key={i} className='text-muted'>
                                {d.data ? moment(d.data).format('DD/MM/YYYY') : 's/ data'} · {d.historico || 'saldo'}: PDF {brl(d.saldoInformado)} × lido {brl(d.saldoCalculado)}
                            </div>
                        ))}
                        {conferencia.linhasNaoConferidas > 0 && conferencia.tipo !== 'SEM_SALDO' && (
                            <div className='text-muted'>{conferencia.linhasNaoConferidas} lançamento(s) depois do último saldo impresso, sem como conferir.</div>
                        )}
                        <div className='d-flex gap-2 mt-2'>
                            <button className='btn btn-sm btn-primary py-1' disabled={ocupado} onClick={() => handleConfirmarPdf(arquivo, 'IMPORTAR')}>
                                Importar mesmo assim
                            </button>
                            {conferencia.lotesDivergentes.length > 0 && (
                                <button className='btn btn-sm btn-light-primary py-1' disabled={ocupado} onClick={() => handleConfirmarPdf(arquivo, 'RELER')}>
                                    Reler páginas divergentes
                                </button>
                            )}
                            <button className='btn btn-sm btn-light-danger py-1' disabled={ocupado} onClick={() => handleConfirmarPdf(arquivo, 'DESCARTAR')}>
                                Descartar
                            </button>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <Modal size='lg' centered show={show} onHide={onHide}>
            <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                <div className='modal-header'>
                    <h5 className='modal-title titulo-modal'>Importação de Extrato Bancário</h5>
                    <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                        <i onClick={onHide} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                    </div>
                </div>

                <div className='modal-body'>
                    {/* Upload area */}
                    <div className='card card-body mb-4 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                        <div className='fw-bold mb-1' style={{ fontSize: 13 }}>Enviar arquivos de extrato</div>
                        <div className='text-muted mb-2' style={{ fontSize: 11 }}>
                            Formatos aceitos: .ofx (Open Financial Exchange) e .pdf. O PDF é lido por IA: você vê a previsão de custo e decide se processa ou descarta.
                        </div>
                        <div className='d-flex align-items-center gap-3'>
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept={ACCEPTED_EXTENSIONS}
                                multiple
                                className='form-control form-control-sm'
                                style={{ maxWidth: 320 }}
                                disabled={ocupado}
                                onChange={handleUpload}
                            />
                            {uploading && <span className='spinner-border spinner-border-sm text-primary' />}
                        </div>
                    </div>

                    {/* File list */}
                    {loading ? (
                        <div className='d-flex justify-content-center py-4'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : arquivos.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d', fontSize: 13 }}>
                            Nenhum arquivo enviado ainda. Selecione os extratos bancários acima.
                        </div>
                    ) : (
                        <>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                <span className='fw-bold' style={{ fontSize: 13 }}>Arquivos enviados</span>
                                <span className='text-muted' style={{ fontSize: 12 }}>{total} arquivo(s)</span>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mb-0'>
                                    <thead>
                                        <tr className='fw-bold text-muted' style={{ fontSize: 12 }}>
                                            <th style={{ width: 32 }}></th>
                                            <th>Nome</th>
                                            <th className='text-nowrap'>Enviado em</th>
                                            <th className='text-nowrap'>Custo da IA</th>
                                            <th className='text-center'>Status</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {arquivos.map(arquivo => {
                                            const st = statusInfo(arquivo);
                                            const custo = custoInfo(arquivo);
                                            const pdf = arquivo.excluido ? null : arquivo.pdf;
                                            const selecionavel = !arquivo.excluido && !arquivo.arquivoProcessando && !pdfEmAndamento(arquivo);
                                            const decidindo = decidindoId === arquivo.id;
                                            return (
                                                <Fragment key={arquivo.id}>
                                                    <tr style={arquivo.excluido ? { opacity: 0.6 } : undefined}>
                                                        <td className='text-center'>
                                                            <input
                                                                type='checkbox'
                                                                className='form-check-input'
                                                                checked={selecionados.has(arquivo.id)}
                                                                disabled={!selecionavel || ocupado}
                                                                onChange={() => toggleSelecionado(arquivo.id)}
                                                            />
                                                        </td>
                                                        <td style={{ fontSize: 12, maxWidth: 220 }}>
                                                            <span title={arquivo.nomeOriginal} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {arquivo.nomeOriginal}
                                                            </span>
                                                            {pdf && pdf.totalPaginas > 0 && (
                                                                <span className='text-muted' style={{ fontSize: 10 }}>
                                                                    {pdf.totalPaginas} página(s)
                                                                    {pdf.status === 'IMPORTADO' && pdf.conferencia ? ` · ${CONFERENCIA_LABEL[pdf.conferencia.tipo]}` : ''}
                                                                </span>
                                                            )}
                                                            {pdf?.status === 'ERRO' && pdf.mensagem && (
                                                                <span style={{ display: 'block', fontSize: 10, color: '#d9534f', whiteSpace: 'normal' }}>{pdf.mensagem}</span>
                                                            )}
                                                            {arquivo.contaBancaria?.motivo && pdf?.status !== 'ERRO' && (
                                                                <span style={{ display: 'block', fontSize: 10, color: '#d9534f', whiteSpace: 'normal' }}>{arquivo.contaBancaria.motivo}</span>
                                                            )}
                                                            {arquivo.contaBancaria?.aviso && (
                                                                <span style={{ display: 'block', fontSize: 10, color: '#fd7e14', whiteSpace: 'normal' }}>{arquivo.contaBancaria.aviso}</span>
                                                            )}
                                                            {(arquivo.contaBancaria?.situacao === 'AGUARDANDO_CONTA' || arquivo.contaBancaria?.situacao === 'AGUARDANDO_SALDO') && (
                                                                <span style={{ display: 'block', fontSize: 10, color: '#fd7e14', whiteSpace: 'normal' }}>Resolva no Módulo Bancário.</span>
                                                            )}
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {moment(arquivo.dataCadastro).format('DD/MM/YYYY HH:mm')}
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {custo.texto}
                                                            {custo.detalhe && <div className='text-muted' style={{ fontSize: 10 }}>{custo.detalhe}</div>}
                                                        </td>
                                                        <td className='text-center'>
                                                            <span style={{
                                                                fontSize: 10,
                                                                fontWeight: 'bold',
                                                                color: st.color,
                                                                backgroundColor: st.bg,
                                                                padding: '2px 8px',
                                                                borderRadius: 4,
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {st.label}
                                                            </span>
                                                            {arquivo.excluido && (
                                                                <div style={{ fontSize: 10, color: '#6c757d', marginTop: 2, whiteSpace: 'nowrap' }}>
                                                                    por {arquivo.excluidoPorNome || '—'}
                                                                    {arquivo.excluidoEm ? ` em ${moment(arquivo.excluidoEm).format('DD/MM/YYYY')}` : ''}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className='text-end text-nowrap'>
                                                            {decidindo && <span className='spinner-border spinner-border-sm text-primary' />}
                                                            {!decidindo && pdf?.status === 'AGUARDANDO_DECISAO' && (
                                                                <>
                                                                    <button className='btn btn-sm btn-primary py-1 px-3 me-1' disabled={ocupado} onClick={() => handleDecidirPdf(arquivo, true)}>
                                                                        Processar
                                                                    </button>
                                                                    <button className='btn btn-sm btn-light-danger py-1 px-3' disabled={ocupado} onClick={() => handleDecidirPdf(arquivo, false)}>
                                                                        Descartar
                                                                    </button>
                                                                </>
                                                            )}
                                                            {!decidindo && (pdf?.status === 'APROVADO' || pdf?.status === 'ERRO') && (
                                                                <button className='btn btn-sm btn-light-danger py-1 px-3' disabled={ocupado} onClick={() => handleDecidirPdf(arquivo, false)}>
                                                                    Descartar
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {pdf?.status === 'AGUARDANDO_CONFIRMACAO' && pdf.conferencia && renderConferencia(arquivo, pdf.conferencia)}
                                                </Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className='d-flex justify-content-between align-items-center mt-2'>
                                    <span style={{ fontSize: 12, color: '#6c757d' }}>Página {page} de {totalPages}</span>
                                    <div className='d-flex gap-1'>
                                        <button className='btn btn-sm btn-light' disabled={page === 1} onClick={() => handlePage(page - 1)}>‹</button>
                                        <button className='btn btn-sm btn-light' disabled={page === totalPages} onClick={() => handlePage(page + 1)}>›</button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className='modal-footer d-flex justify-content-between'>
                    <div className='d-flex gap-2'>
                        <button className='btn btn-light' onClick={onHide} disabled={ocupado}>
                            Fechar
                        </button>
                        <button
                            className='btn btn-danger'
                            disabled={selecionados.size === 0 || ocupado}
                            onClick={handleExcluir}
                        >
                            {excluindo ? (
                                <>
                                    <span className='spinner-border spinner-border-sm me-2' />
                                    Excluindo...
                                </>
                            ) : `Excluir selecionado(s)${selecionados.size > 0 ? ` (${selecionados.size})` : ''}`}
                        </button>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                        {temPdfSemDecisao && (
                            <span className='text-muted' style={{ fontSize: 11, maxWidth: 220, textAlign: 'right' }}>
                                Decida os PDFs (processar ou descartar) para liberar o início.
                            </span>
                        )}
                        <button
                            className='btn btn-primary'
                            disabled={!temPendentes || temPdfSemDecisao || ocupado}
                            onClick={handleIniciarProcessamento}
                        >
                            {iniciando ? (
                                <>
                                    <span className='spinner-border spinner-border-sm me-2' />
                                    Iniciando...
                                </>
                            ) : 'Iniciar Processamento'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default ModalExtratoBancario;
